import { useQuery } from '@tanstack/react-query';
import {
  fetchMeasurementSessionDefaults,
  fetchTestKitProfiles,
} from '../services/measurementService';
import type {
  MeasurementSessionDefaultsResponse,
  TestKitProfileResponse,
} from '../types/measurement';

export function useMeasurementSessionDefaults(aquariumId: string) {
  return useQuery<MeasurementSessionDefaultsResponse, Error>({
    queryKey: ['measurementSessionDefaults', aquariumId],
    queryFn: () => fetchMeasurementSessionDefaults(aquariumId),
    enabled: aquariumId.length > 0,
  });
}

export function useTestKitProfiles() {
  return useQuery<TestKitProfileResponse[], Error>({
    queryKey: ['testKitProfiles'],
    queryFn: () => fetchTestKitProfiles(),
  });
}

export interface MeasurementSessionData {
  readonly sessionDefaults: MeasurementSessionDefaultsResponse | undefined;
  readonly testKitProfiles: TestKitProfileResponse[] | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isSuccess: boolean;
  /** Re-fetches whichever of the two queries failed (or both). */
  readonly refetch: () => Promise<unknown>;
}

export function useMeasurementSessionData(aquariumId: string): MeasurementSessionData {
  const sessionDefaultsQuery = useMeasurementSessionDefaults(aquariumId);
  const testKitProfilesQuery = useTestKitProfiles();

  const isLoading = sessionDefaultsQuery.isLoading || testKitProfilesQuery.isLoading;
  const isError = sessionDefaultsQuery.isError || testKitProfilesQuery.isError;
  const error = sessionDefaultsQuery.error ?? testKitProfilesQuery.error ?? null;
  const isSuccess = sessionDefaultsQuery.isSuccess && testKitProfilesQuery.isSuccess;

  return {
    sessionDefaults: sessionDefaultsQuery.data,
    testKitProfiles: testKitProfilesQuery.data,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch: () => Promise.all([sessionDefaultsQuery.refetch(), testKitProfilesQuery.refetch()]),
  };
}
